import { useState } from "react";
import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import { useMutation } from "@apollo/client";
import {
  DELETE_PDR_CREDIT_MUTATION,
  GET_PDR_HISTORY_QUERY,
  GET_PDR_REQUIREMENTS_QUERY
} from "@/pages/people/[pgaId]/education/queries";
import { DELETE_ACTIVITY_TYPE_PARTICIPATION_MUTATION } from "../mutations";
import { showSnackbar } from "@/lib/snackbarUtils";
import { useNavigate, useParams } from "react-router-dom";
import Banner from "@/components/Banner";

export default function DeleteParticipationModal({
  onDismiss,
  activityId,
  activityParticipationId,
  isTypeLevelParticipation,
  onSuccessNavigate,
  additionalRefetchQueries
}: {
  onDismiss: () => void;
  /** CCMS credit ID — used for legacy credit-only deletes via deletePdrCredit. */
  activityId?: string;
  /** Membership::ActivityParticipation ID — used for type-level deletes. */
  activityParticipationId?: string;
  isTypeLevelParticipation?: boolean;
  /** Optional: custom navigation on success. Defaults to people education page. */
  onSuccessNavigate?: () => void;
  /** Optional: additional queries to refetch after deleting participation. */
  additionalRefetchQueries?: any[];
}) {
  const { pgaId } = useParams();
  const [bannerError, setBannerError] = useState("");
  const navigate = useNavigate();
  const refetchQueries = [GET_PDR_HISTORY_QUERY, GET_PDR_REQUIREMENTS_QUERY, ...(additionalRefetchQueries || [])];

  const handleSuccess = () => {
    showSnackbar("Participation deleted");
    onDismiss();
    if (onSuccessNavigate) {
      onSuccessNavigate();
    } else {
      navigate(`/people/${pgaId}/education/professional-development`);
    }
  };

  const [deleteActivityTypeParticipation, { loading: deletingTypeLevel }] = useMutation(
    DELETE_ACTIVITY_TYPE_PARTICIPATION_MUTATION,
    {
      refetchQueries,
      onCompleted: (data) => {
        if (data.deleteActivityTypeParticipation.success) {
          handleSuccess();
        } else {
          setBannerError(data.deleteActivityTypeParticipation.message);
        }
      },
      onError: (error) => {
        setBannerError(`Error: ${error.message}`);
      }
    }
  );

  const [deletePdrCredit, { loading: deletingLegacy }] = useMutation(DELETE_PDR_CREDIT_MUTATION, {
    refetchQueries,
    onCompleted: (data) => {
      if (data.deletePdrCredit.success) {
        handleSuccess();
      } else {
        setBannerError(data.deletePdrCredit.message);
      }
    },
    onError: (error) => {
      setBannerError(`Error: ${error.message}`);
    }
  });

  const loading = deletingTypeLevel || deletingLegacy;

  if (isTypeLevelParticipation && !activityParticipationId) {
    return null;
  }

  if (!isTypeLevelParticipation && !activityId) {
    return null;
  }

  const handleDelete = async () => {
    if (isTypeLevelParticipation) {
      await deleteActivityTypeParticipation({
        variables: {
          id: activityParticipationId!
        }
      });
      return;
    }

    await deletePdrCredit({
      variables: {
        id: activityId!
      }
    });
  };

  return (
    <Dialog
      open={true}
      onClosed={onDismiss}
      headline="Delete Participation"
      className="w-[490px] p-6 pr-2"
      actions={
        <>
          <Button variant="text" onClick={onDismiss} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="filled"
            theme="error"
            trailingIcon={false}
            icon="delete"
            onClick={handleDelete}
            disabled={loading}
            data-testid="confirm-delete-participation"
          >
            Delete
          </Button>
        </>
      }
    >
      {!!bannerError && (
        <div className="mb-6">
          <Banner inline variant="error" description="The participation could not be deleted." subText={bannerError} />
        </div>
      )}
      <p>Are you sure you want to delete this person&apos;s activity participation? This cannot be undone.</p>
    </Dialog>
  );
}
